import type {
  CancelDrop,
  CollisionDetection,
  DropAnimation,
  KeyboardCoordinateGetter,
  Modifiers,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { SortingStrategy } from '@dnd-kit/sortable';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { nanoid } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { coordinateGetter as multipleContainersCoordinateGetter } from 'components/DragAndDrop/Sortable/multipleContainersKeyboardCoordinates';
import { Container, Item } from 'components/DragAndDrop/components';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import { EInterviewSummaryStandardBlockNames } from 'types/template/templateInterviewSummary.types';
import type { TSortableContainerItem, TSortableInput } from '..';
import SortableItemv2 from './SortableItemv2';
import DroppableContainerv2 from './DroppableContainerv2';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

interface Props {
  items: TSortableInput;
  onShouldUpdate?: (items: any, containers: string[]) => void;
  headerContent?: (data: any) => ReactNode;
  itemContent?: (data: any) => ReactNode;
  addContainer: {
    enabled: boolean;
    buttonText?: string;
  };
  disabled?: boolean;

  adjustScale?: boolean;
  cancelDrop?: CancelDrop;
  columns?: number;
  containerStyle?: CSSProperties;
  coordinateGetter?: KeyboardCoordinateGetter;
  getItemStyles?(args: {
    value: UniqueIdentifier;
    index: number;
    overIndex: number;
    isDragging: boolean;
    containerId: UniqueIdentifier;
    isSorting: boolean;
    isDragOverlay: boolean;
  }): CSSProperties;
  wrapperStyle?(args: { index: number }): CSSProperties;
  itemCount?: number;
  handle?: boolean;
  renderItem?: any;
  strategy?: SortingStrategy;
  modifiers?: Modifiers;
  minimal?: boolean;
  scrollable?: boolean;
  vertical?: boolean;
}

const TRASH_ID = 'void';
const PLACEHOLDER_ID = 'placeholder';
const DATA_UPDATE_DELAY_MS = 400;

const SortableContainers = ({
  items: initialItems,
  onShouldUpdate,
  headerContent,
  itemContent,
  addContainer = {
    enabled: false,
    buttonText: '+ Add',
  },
  disabled = false,

  adjustScale = false,
  cancelDrop,
  columns,
  handle = false,
  containerStyle,
  coordinateGetter = multipleContainersCoordinateGetter,
  getItemStyles = () => ({}),
  wrapperStyle = () => ({}),
  minimal = false,
  modifiers,
  renderItem,
  strategy = verticalListSortingStrategy,
  vertical = false,
  scrollable,
}: Props) => {
  const [items, setItems] = useState<TSortableInput>(initialItems ?? undefined);
  const [clonedItems, setClonedItems] = useState<TSortableInput | null>(null);
  const [containers, setContainers] = useState(Object.keys(items));
  const [shouldUpdate, setShouldUpdate] = useState<boolean>(false);
  useEffect(() => {
    const tempItems: TSortableInput = {};
    const keys: string[] = [];
    if (initialItems) {
      Object.keys(initialItems).map((key) => {
        if (initialItems[key]) {
          keys.push(key);
          tempItems[key] = initialItems[key];
        }
      });
      setItems(tempItems);
      setContainers(keys);
    }
  }, [initialItems]);

  useEffect(() => {
    if (!items || !containers) return;

    if (shouldUpdate && onShouldUpdate) {
      // Small delay to update the actual data
      setTimeout(() => {
        onShouldUpdate(items, containers);
      }, DATA_UPDATE_DELAY_MS);

      setShouldUpdate(false);
    }
  }, [shouldUpdate]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const lastOverId = useRef<string | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId ? containers.includes(activeId) : false;

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id') as string;

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections;
        }

        if (overId in items) {
          const containerItems = items[overId]?.items ?? [];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container

            const droppableContainers = args.droppableContainers.filter((container) => {
              const x = containerItems.find((x) => x.id === container.id);
              if (
                container.id !== overId &&
                // containerItems.includes(container.id)
                x
              ) {
                return true;
              }
              return false;
            });

            overId = closestCenter({
              ...args,
              droppableContainers,
            })[0]?.id as string;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
  );

  const findContainer = (id: string | UniqueIdentifier) => {
    if (id in items) {
      // Container
      return id;
    }

    // Must be a container item

    // return Object.keys(items).find((key) => items[key].id === id);
    return Object.keys(items).find((key) => items[key]?.items.find((item) => item.id === id));
  };

  const getIndex = (id: string | UniqueIdentifier) => {
    const container = findContainer(id);
    if (!container) {
      return -1;
    }
    // const index = items[container].indexOf(id);
    const index = items[container]?.items.findIndex((x) => x.id === id) ?? -1;
    return index;
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }
    setActiveId(null);
    setClonedItems(null);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        console.log('onDragStart active.id', active.id);
        setActiveId(active.id as string);
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        const overId = (over?.id as string) || null;

        if (overId == null || overId === TRASH_ID || active.id in items) {
          return;
        }

        /*
          overId: can be an item.id in container.items
        */

        const overContainerId = findContainer(overId);
        const activeContainerId = findContainer(active.id);

        if (!overContainerId || !activeContainerId) {
          return;
        }

        if (activeContainerId !== overContainerId) {
          setItems((items) => {
            const activeContainer = items[activeContainerId];
            const overContainer = items[overContainerId];

            if (!activeContainer || !overContainer) {
              return items;
            }

            const overIndex = overContainer.items.findIndex((x) => x.id === overId);
            const activeIndex = activeContainer.items.findIndex((x) => x.id === active.id);
            const activeItem = activeContainer.items[activeIndex];

            if (!activeItem) {
              return items;
            }

            let newIndex: number;

            if (overId in items) {
              newIndex = overContainer.items.length + 1;
            } else {
              const isBelowOverItem =
                over &&
                active.rect.current.translated &&
                active.rect.current.translated.top > over.rect.top + over.rect.height;

              const modifier = isBelowOverItem ? 1 : 0;

              newIndex = overIndex >= 0 ? overIndex + modifier : overContainer.items.length + 1;
            }

            recentlyMovedToNewContainer.current = true;

            return {
              ...items,
              [activeContainerId]: {
                ...activeContainer,
                items: activeContainer.items.filter((x) => x.id !== active.id),
              },
              [overContainerId]: {
                ...overContainer,
                items: [...overContainer.items.slice(0, newIndex), activeItem, ...overContainer.items.slice(newIndex)],
              },
            };
          });
        }
      }}
      onDragEnd={({ active, over }) => {
        if (active.id in items && over?.id) {
          // Moving containers
          const activeIndex = containers.indexOf(active.id as string);
          const overIndex = containers.indexOf(over.id as string);
          const order = arrayMove(containers, activeIndex, overIndex);
          setContainers(order);

          return;
        }

        // Moving container items
        const activeContainerId = findContainer(active.id);

        if (!activeContainerId) {
          setActiveId(null);
          return;
        }

        const overId = over?.id;
        if (overId == null) {
          setActiveId(null);
          return;
        }

        const overContainerId = findContainer(overId);

        if (overId === TRASH_ID) {
          setItems((items) => ({
            ...items,
            [activeContainerId]: {
              ...(items[activeContainerId] ?? { id: '', headerName: '', items: [], isDefault: false }),
              items: items[activeContainerId]?.items.filter((x) => x.id !== activeId) ?? [],
            },
          }));

          setActiveId(null);
          return;
        }

        if (overId === PLACEHOLDER_ID) {
          const newContainerId = nanoid();

          unstable_batchedUpdates(() => {
            setContainers((containers) => [...containers, newContainerId]);
            setItems((items) => ({
              ...items,
              [activeContainerId]: {
                ...(items[activeContainerId] ?? { id: '', headerName: '', items: [], isDefault: false }),
                items: items[activeContainerId]?.items.filter((x) => x.id !== activeId) ?? [],
              },
              [newContainerId]: {
                id: newContainerId,
                headerName: newContainerId,
                items: [],
                isDefault: false,
              },
            }));

            setActiveId(null);
          });
          return;
        }

        if (overContainerId) {
          const activeContainer = items[activeContainerId];
          const overContainer = items[overContainerId];
          const activeIndex = activeContainer?.items.findIndex((x) => x.id === active.id) ?? -1;
          const overIndex = overContainer?.items.findIndex((x) => x.id === overId) ?? -1;
          const move = arrayMove(overContainer?.items ?? [], activeIndex, overIndex);

          setItems((items) => ({
            ...items,
            [overContainerId]: {
              ...(items[overContainerId] ?? { id: '', headerName: '', items: [], isDefault: false }),
              items: move,
            },
          }));
          setShouldUpdate(true);
        }

        setActiveId(null);
      }}
      cancelDrop={cancelDrop}
      onDragCancel={onDragCancel}
      modifiers={modifiers}
    >
      <div
        className={clsx(
          'flex flex-col justify-between gap-4',
          'box-border',
          vertical ? 'grid-flow-row' : 'grid-flow-col',
        )}
      >
        <SortableContext
          items={[...containers, PLACEHOLDER_ID]}
          strategy={vertical ? verticalListSortingStrategy : horizontalListSortingStrategy}
        >
          {containers.map((containerId) => {
            const label = `Container ${containerId}`;

            return (
              <DroppableContainerv2
                // Below props are edited
                key={containerId}
                container={items[containerId] ?? { id: '', headerName: '', items: [], isDefault: false }}
                containerId={containerId}
                headerContent={headerContent?.(items[containerId])}
                // Below props are NOT edited
                label={minimal ? undefined : label}
                columns={columns}
                scrollable={scrollable}
                style={containerStyle}
                unstyled={minimal}
                onRemove={() => handleRemove(containerId)}
              >
                <SortableContext items={items[containerId]?.items ?? []} strategy={strategy}>
                  {items[containerId]?.items.map((item, index) => {
                    return (
                      <SortableItemv2
                        // disabled={isSortingContainer}
                        disabled={disabled ?? isSortingContainer}
                        key={`sortable-item-${item.id}`}
                        id={item.id}
                        item={item}
                        index={index}
                        itemContent={
                          itemContent
                            ? itemContent({
                                item,
                                index,
                                containerId,
                              })
                            : undefined
                        }
                        handle={handle}
                        style={getItemStyles}
                        wrapperStyle={wrapperStyle}
                        renderItem={renderItem}
                        containerId={containerId}
                        getIndex={getIndex}
                      />
                    );
                  })}
                </SortableContext>
              </DroppableContainerv2>
            );
          })}

          {!minimal && addContainer.enabled ? (
            // @ts-ignore
            <DroppableContainerv2
              // Below props are edited
              key={PLACEHOLDER_ID}
              onClick={handleAddColumn}
              placeholder
            >
              {addContainer.buttonText}
            </DroppableContainerv2>
          ) : undefined}
        </SortableContext>
      </div>
      {createPortal(
        <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
          {activeId
            ? containers.includes(activeId)
              ? renderContainerDragOverlay(activeId)
              : renderSortableItemDragOverlay(activeId)
            : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );

  function renderSortableItemDragOverlay(id: UniqueIdentifier) {
    return (
      <Item
        value={id}
        handle={handle}
        style={getItemStyles({
          containerId: findContainer(id) as UniqueIdentifier,
          overIndex: -1,
          index: getIndex(id),
          value: id,
          isSorting: true,
          isDragging: true,
          isDragOverlay: true,
        })}
        wrapperStyle={wrapperStyle({ index: 0 })}
        renderItem={renderItem}
        dragOverlay
      />
    );
  }

  function renderContainerDragOverlay(containerId: string | UniqueIdentifier) {
    return (
      <Container
        label={`Container ${containerId}`}
        columns={columns}
        style={{
          height: '100%',
        }}
        shadow
        unstyled={false}
      >
        {items[containerId]?.items.map((item, index) => (
          <Item
            key={item.id}
            // value={item}
            value={item.id} // TODO: change to item.text?
            handle={handle}
            style={getItemStyles({
              containerId,
              overIndex: -1,
              index: getIndex(item.id),
              // @ts-ignore
              value: item.text,
              isDragging: false,
              isSorting: false,
              isDragOverlay: false,
            })}
            wrapperStyle={wrapperStyle({ index })}
            renderItem={renderItem}
          />
        ))}
      </Container>
    );
  }

  function handleRemove(containerID: UniqueIdentifier) {
    setContainers((containers) => containers.filter((id) => id !== containerID));
  }

  function handleAddColumn() {
    const newBlockId = nanoid();
    const newBlockName = EInterviewSummaryStandardBlockNames.UntitledBlock;

    const newContainer: TSortableContainerItem = {
      id: newBlockId,
      headerName: newBlockName,
      items: [],
      isDefault: false,
    };

    unstable_batchedUpdates(() => {
      setContainers((containers) => [...containers, newBlockId]);
      setItems((items) => ({
        ...items,
        [newBlockId]: newContainer,
      }));
    });
  }
};

export default SortableContainers;
